import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { History, Loader2, RefreshCw } from 'lucide-react';
import { projectId } from '../../utils/supabase/info';
import { bearerAuthHeader } from '../../utils/apiHeaders';

/**
 * The AI call log the server writes for every model call (supabase/functions/server/openai.ts):
 * one row per AI request, with who, which feature, model, tokens and outcome.
 * Prompts and responses are not stored, so there is nothing to show of them.
 */

interface AICallRow {
  id: string;
  timestamp: string;
  companyId: string | null;
  companyName: string | null;
  userId: string | null;
  userName: string | null;
  feature: string;
  model: string;
  outcome: string;
  totalTokens: number;
  costUsd: number;
}

interface AICallSummary {
  calls: number;
  blocked: number;
  failed: number;
  totalTokens: number;
  costUsd: number;
}

interface AICallLogProps {
  /** 'company': one company's calls (company admins, the firm). 'firm': the whole firm (firm admins). */
  scope: 'company' | 'firm';
  companyId?: string;
}

/** Readable names for the purpose tags openai.ts callers use; unknown tags show as-is. */
const FEATURE_LABELS: Record<string, string> = {
  'ai-assistant:chat': 'AI assistant chat',
  'ai-assistant:greeting': 'AI assistant greeting',
  'ai-assistant:tldr': 'AI assistant summary',
  'ai-assistant:quick-sow': 'Quick SOW draft',
  'attachments:analyze-image': 'Attachment analysis (image)',
  'attachments:analyze-data-export': 'Attachment analysis (data export)',
  'ci-detail:analyze-attachment': 'Screenshot analysis',
  'bdm:extract-notes': 'Sales notes extraction',
  'bdm:conversational-extract': 'BDM assistant',
  'consultant-ai:pre-meeting-prep': 'Meeting prep',
  'consultant-ai:analyze-meeting-notes': 'Meeting notes analysis',
  'consultant-ai:extract-discovery-answers': 'Discovery answers from notes',
  'consultant-dashboard:parse-sow-content': 'SOW text parsing',
  'consultant-dashboard:analyze-hlo-gap': 'Objective gap analysis',
  'discovery-context:cross-theme-analysis': 'Cross-theme insights',
  'sow-analysis': 'SOW scope analysis',
  'admin:text-analysis': 'Template scope parsing',
  'insight-engines:change-order': 'Change order detection',
  'journey-metadata:extract': 'Journey banner',
  'onboarding:goals-context': 'Project goals extraction',
  'sow-upload:extract-hlos': 'SOW objectives extraction',
  'ux:analyze-category-answers': 'Answer completeness review',
  'atlassian-jsm:ai-jql-generate': 'JQL draft',
  skip_plan: 'Discovery question skipping',
};

const OUTCOME_LABELS: Record<string, { label: string; variant: 'secondary' | 'outline' | 'destructive' }> = {
  ok: { label: 'Completed', variant: 'secondary' },
  blocked_ai_disabled: { label: 'Blocked: AI off', variant: 'outline' },
  blocked_consultant_only: { label: 'Blocked: consultants only', variant: 'outline' },
  budget_exhausted: { label: 'Blocked: budget', variant: 'outline' },
  started: { label: 'No result recorded', variant: 'outline' },
  no_key: { label: 'Not configured', variant: 'destructive' },
  http_error: { label: 'Provider error', variant: 'destructive' },
  timeout: { label: 'Timed out', variant: 'destructive' },
  network_error: { label: 'Provider unreachable', variant: 'destructive' },
};

const PAGE = 25;

export function AICallLog({ scope, companyId }: AICallLogProps) {
  const [rows, setRows] = useState<AICallRow[]>([]);
  const [summary, setSummary] = useState<AICallSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const url =
    scope === 'company'
      ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/audit/company/${companyId}?limit=200`
      : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai/audit/firm?limit=200`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url, { headers: { ...bearerAuthHeader() } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || 'Could not load the AI call log');
        return;
      }
      setRows(data.records || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('[AICallLog] load failed:', err);
      setError('Could not load the AI call log');
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (scope === 'company' && !companyId) return;
    load();
  }, [scope, companyId, load]);

  const visible = showAll ? rows : rows.slice(0, PAGE);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="size-5 text-purple-500" />
              AI Call Log
            </CardTitle>
            <CardDescription>
              {scope === 'company'
                ? 'Every AI request made for this company: who made it, the feature, the model and tokens used. Requests blocked because AI is off are listed too. Prompts and responses are not stored.'
                : "Every AI request made by your firm: for each client company, and for work with no company yet, such as sales notes. Prompts and responses are not stored."}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-destructive">{error}</p>}

        {summary && !error && (
          <p className="text-muted-foreground">
            {summary.calls} request{summary.calls === 1 ? '' : 's'}
            {summary.blocked > 0 && `, ${summary.blocked} blocked`}
            {summary.failed > 0 && `, ${summary.failed} failed`}
            {` · ${summary.totalTokens.toLocaleString()} tokens · about $${summary.costUsd.toFixed(2)}`}
            {rows.length >= 200 && ' (latest 200)'}
          </p>
        )}

        {!loading && !error && rows.length === 0 && (
          <p className="text-muted-foreground">No AI requests recorded yet.</p>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Feature</TableHead>
                  {scope === 'firm' && <TableHead>Company</TableHead>}
                  <TableHead>By</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead>Outcome</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((row) => {
                  const outcome = OUTCOME_LABELS[row.outcome] ?? { label: row.outcome, variant: 'outline' as const };
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">{new Date(row.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{FEATURE_LABELS[row.feature] ?? row.feature}</TableCell>
                      {scope === 'firm' && (
                        <TableCell>{row.companyId ? row.companyName ?? row.companyId : 'No company (firm work)'}</TableCell>
                      )}
                      <TableCell>{row.userName ?? (row.userId ? row.userId : 'Not signed in')}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.model}</TableCell>
                      <TableCell className="text-right">{row.totalTokens ? row.totalTokens.toLocaleString() : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={outcome.variant}>{outcome.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {rows.length > PAGE && (
          <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show fewer' : `Show all ${rows.length}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
